import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { TerminalWindow } from '@phosphor-icons/react';
import { api } from '~/lib/api-client';
import { skillKeys, chatCommandKeys } from '~/lib/query-keys';
import { slashMenuPosition, type SlashMenuPosition } from './slash-menu-position';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '~/components/ui/command';

interface SlashCommand {
  command: string;
  name: string;
  description: string;
  slug: string;
}

interface RawSkill {
  slug: string;
  name?: string;
  description?: string;
  command?: string;
}

/** Wire shape of `GET /api/chat/commands` (src/agents/commands-routes.ts). */
interface RawChatCommand {
  command: string;
  name: string;
  description: string;
}

/**
 * Merge the built-in quick-capture commands with the tenant's skill-derived
 * ones. Built-ins always come first (their fixed menu order), then skills
 * sorted alphabetically by command word.
 *
 * FRONT-1349 review fix: de-duplication (and, at the render site, the React
 * list key) must match on `command` — the word that actually decides
 * dispatch server-side (`resolveSkillSlashCommandUnlessQuickCapture`) — not
 * `slug`. A built-in's `slug` is set to its command word (see below), so a
 * skill whose own SLUG happens to equal a built-in's command word (e.g.
 * slug `"note"` with an unrelated command like `"capture"`) is a real,
 * distinct command that must survive; only a `command`-word collision means
 * the built-in already claims that dispatch and the skill entry is dead.
 * Pulled out as a pure function (rather than inlined in the hook's
 * `useMemo`) so this merge/precedence logic is unit-testable without
 * rendering the hook.
 *
 * FRONT-1349 adversarial-review fix: a built-in collision isn't the only
 * way two entries can share a `command` — two tenant SKILLS can each
 * declare the same command word (nothing before this stopped that). That
 * used to survive as two list entries with an identical `command`, which
 * both collided on the render site's React key and broke keyboard
 * selection (`onValueChange`'s `filtered.findIndex(c => c.command === v)`
 * always resolves to the FIRST match, so arrowing to the second entry and
 * pressing Enter fired the first). Server-side dispatch
 * (`chat-proxy.ts`'s `cmdMap`) is built the same way — `new Map(array.map(...))`
 * — which keeps only the LAST entry for a repeated key; showing two menu
 * rows for a command that only ever resolves to one of them is misleading
 * regardless of which the user picks. Deduping here with the identical
 * "last one wins" construction keeps the menu honest about what actually
 * dispatches, and incidentally restores the React-key/keyboard-selection
 * invariant since `command` is unique in the returned list either way.
 */
export function mergeSlashCommands(
  skills: readonly RawSkill[],
  builtinCommands: readonly RawChatCommand[],
): SlashCommand[] {
  const builtins: SlashCommand[] = builtinCommands.map((c) => ({
    command: c.command,
    name: c.name,
    description: c.description,
    slug: c.command,
  }));
  const builtinWords = new Set(builtins.map((c) => c.command));
  // A built-in always wins the same command word server-side (FRONT-1349's
  // resolveSkillSlashCommandUnlessQuickCapture) — drop a same-named skill
  // entry here too, so the menu never advertises something that could
  // never actually fire. Then fold any remaining skill-vs-skill collision
  // down to one entry per command word — same last-one-wins rule as the
  // server's own dispatch map, via a Map keyed on `command` (a later
  // `.set()` on an existing key overwrites the value but keeps the key's
  // original insertion slot, which is fine since the result is re-sorted
  // below anyway).
  const bySkillCommand = new Map<string, SlashCommand>();
  for (const s of skills) {
    if (typeof s.command !== 'string' || s.command.length === 0) continue;
    const command = s.command.replace(/^\/+/, '');
    if (builtinWords.has(command)) continue;
    bySkillCommand.set(command, {
      command,
      name: s.name ?? s.slug,
      description: s.description ?? '',
      slug: s.slug,
    });
  }
  const fromSkills: SlashCommand[] = [...bySkillCommand.values()].sort((a, b) =>
    a.command.localeCompare(b.command),
  );
  // Built-ins first, in their fixed menu order (see QUICK_CAPTURE_COMMANDS).
  return [...builtins, ...fromSkills];
}

interface UseSlashMenuArgs {
  value: string;
  onPick: (command: string) => void;
}

/**
 * Claude-Code-style slash command menu for the chat input.
 *
 * Use:
 *   const slash = useSlashMenu({ value, onPick: (cmd) => setText(`/${cmd} `) });
 *   <Textarea onKeyDown={slash.onKeyDown} ... />
 *   {slash.menu}
 *
 * Opens whenever the input starts with `/`. Lists every skill whose SKILL.md
 * declares a `command:` field. Up/down navigates, Enter/Tab selects, Esc closes.
 *
 * ONE REQUIREMENT ON THE CALLER (FRONT-1355): `{slash.menu}` must go inside an
 * element that is `position: relative` and is the composer's own box. The menu
 * itself is portalled out, but it leaves an `absolute inset-0` stand-in behind
 * to measure — so a non-positioned or oversized wrapper produces a menu the
 * width of the wrong thing rather than an obviously broken sibling.
 */
export function useSlashMenu({ value, onPick }: UseSlashMenuArgs) {
  const [closed, setClosed] = useState(false); // user pressed Esc this token
  const [active, setActive] = useState(0);

  const { data: skills = [] } = useQuery({
    queryKey: skillKeys.list(),
    queryFn: () =>
      api
        .get<RawSkill[] | { skills: RawSkill[] }>('/api/skills')
        .then((d) => (Array.isArray(d) ? d : d.skills ?? [])),
    staleTime: 60_000,
  });

  // FRONT-1349: the three built-in quick-capture commands (/note, /task,
  // /memory) — server-driven like the skill list above rather than
  // hardcoded here, so the menu never drifts from what chat-proxy.ts
  // actually resolves (src/agents/quick-capture.ts).
  const { data: builtinCommands = [] } = useQuery({
    queryKey: chatCommandKeys.list(),
    queryFn: () =>
      api
        .get<{ commands: RawChatCommand[] }>('/api/chat/commands')
        .then((d) => d.commands ?? []),
    staleTime: 60_000,
  });

  const all = useMemo<SlashCommand[]>(
    () => mergeSlashCommands(skills as RawSkill[], builtinCommands as RawChatCommand[]),
    [skills, builtinCommands],
  );

  const looksLikeSlash = value.startsWith('/');
  const query = looksLikeSlash ? value.slice(1).split(/\s/)[0].toLowerCase() : '';
  // Reset Esc-suppression when the user clears the input or starts a new token.
  useEffect(() => {
    if (!looksLikeSlash) setClosed(false);
  }, [looksLikeSlash]);

  const filtered = useMemo(() => {
    if (!looksLikeSlash) return [];
    if (!query) return all;
    const prefix: SlashCommand[] = [];
    const subCmd: SlashCommand[] = [];
    const subName: SlashCommand[] = [];
    for (const c of all) {
      const cmd = c.command.toLowerCase();
      const name = c.name.toLowerCase();
      if (cmd.startsWith(query)) prefix.push(c);
      else if (cmd.includes(query)) subCmd.push(c);
      else if (name.includes(query)) subName.push(c);
    }
    return [...prefix, ...subCmd, ...subName];
  }, [all, looksLikeSlash, query]);

  useEffect(() => {
    setActive(0);
  }, [query, filtered.length]);

  // Open the menu whenever the input starts with `/` — even when no skills
  // declare a `command:`. An empty menu with an onboarding hint is better
  // than nothing rendering, because the user typed `/` *expecting* the
  // affordance to exist; silent absence reads as "broken".
  const isOpen = looksLikeSlash && !closed;
  const isEmpty = all.length === 0;

  const pick = useCallback(
    (cmd: string) => {
      onPick(cmd);
      setClosed(true);
    },
    [onPick],
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (!isOpen) return;
      // Empty-state menu: only Escape is interactive; everything else falls
      // through to the textarea (let the user keep typing).
      if (filtered.length === 0) {
        if (e.key === 'Escape') {
          e.preventDefault();
          setClosed(true);
        }
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive((a) => (a + 1) % filtered.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((a) => (a - 1 + filtered.length) % filtered.length);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const choice = filtered[active];
        if (choice) pick(choice.command);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setClosed(true);
      }
    },
    [active, filtered, isOpen, pick],
  );

  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setClosed(true);
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // FRONT-1355: the menu is portalled to the document root so no ancestor's
  // `overflow-hidden` can clip it (the chat modal's card has exactly that, and
  // in a short window it cut the top off the list). What stays behind, in the
  // composer, is this zero-height invisible box: it inherits the composer's
  // own position and width, and measuring it is how the portalled menu knows
  // where to sit.
  const anchorRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<SlashMenuPosition | null>(null);
  // Where the menu is portalled to. The nearest dialog when there is one,
  // otherwise the document body — see the note in the render below.
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(null);
      setPortalTarget(null);
      return;
    }
    const el = anchorRef.current;
    if (!el) return;
    setPortalTarget((el.closest('[role="dialog"]') as HTMLElement | null) ?? document.body);
    const measure = () => {
      const r = el.getBoundingClientRect();
      const next = slashMenuPosition(
        { top: r.top, bottom: r.bottom, left: r.left, width: r.width },
        window.innerHeight,
      );
      // Only re-render on a real move. The scroll listener below fires on
      // every frame of any scroll anywhere in the document, and this hook
      // lives inside a large composer component.
      setPosition((prev) =>
        prev &&
        prev.placement === next.placement &&
        prev.left === next.left &&
        prev.width === next.width &&
        prev.top === next.top &&
        prev.bottom === next.bottom &&
        prev.maxHeight === next.maxHeight
          ? prev
          : next,
      );
    };
    measure();
    // The composer does not merely move with the page — it CHANGES SIZE in
    // place: its textarea grows as the user types (`field-sizing-content`, up
    // to 200px), an attachment row appears, a banner opens above it. The old
    // `absolute bottom-full` markup tracked all of that for free; a
    // viewport-positioned portal has to be told. Without this the menu stays
    // where it was and ends up sitting on top of the very text being typed —
    // which is the normal path for `/note`, `/task` and `/memory`, all of
    // which are "command plus a sentence".
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    window.addEventListener('resize', measure);
    // Capture phase: the composer can sit inside a scrolling panel, and a
    // scroll event on that panel does not bubble to the window.
    window.addEventListener('scroll', measure, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [isOpen]);

  // cmdk drives its active item from the `value` prop; we keep our own
  // `active` index (since keyboard focus stays in the textarea) and mirror
  // it into cmdk via the controlled value.
  const activeValue = filtered[active]?.command ?? '';

  const menu = isOpen ? (
    <>
      {/* The measuring stand-in — stays in the composer, renders nothing. */}
      <div ref={anchorRef} aria-hidden className="pointer-events-none absolute inset-0" />
      {portalTarget &&
        createPortal(
    <div
      ref={ref}
      className="fixed z-50 flex flex-col"
      style={{
        left: position?.left ?? 0,
        width: position?.width ?? 0,
        ...(position?.placement === 'below'
          ? { top: position.top }
          : { bottom: position?.bottom ?? 0 }),
        maxHeight: position?.maxHeight ?? 0,
        // Until the first measurement lands there is no honest place to put
        // this, and a frame at the wrong coordinates reads as a flicker.
        visibility: position ? 'visible' : 'hidden',
      }}
    >
      <Command
        shouldFilter={false}
        value={activeValue}
        onValueChange={(v) => {
          const i = filtered.findIndex((c) => c.command === v);
          if (i >= 0) setActive(i);
        }}
        className="flex min-h-0 flex-col rounded-lg border border-border bg-popover shadow-lg"
      >
        <div className="flex items-center gap-2 px-3 pt-2 pb-1.5 text-[length:var(--text-2xs)] uppercase tracking-wider text-muted-foreground">
          <TerminalWindow size={12} weight="bold" />
          <span>Commands</span>
          <span className="ml-auto text-[length:var(--text-2xs)] normal-case tracking-normal text-muted-foreground/70">
            ↑↓ navigate · ↵ select · esc close
          </span>
        </div>
        {/* Height comes from the portal wrapper's measured cap, not a fixed
            class — the point of FRONT-1355 is that the list uses whatever the
            window actually has. The design system's own `max-h-72` (252px at
            this app's 14px root) has to be lifted INLINE: passing `max-h-none`
            leaves both classes on the element, and the stylesheet order — not
            the class order — decides, so the 252px cap kept winning and the
            list still scrolled with 70px of free screen right above it. */}
        {/* FRONT-2185 review round 2: `data-state="open"` is a queryable open
            marker for the operator panel's Escape handler
            (`operator-panel.tsx`'s `hasOpenTransientLayer` query), which
            needs to know this menu owns Escape before the textarea's own
            `onKeyDown` (where this menu's key handling actually lives) gets
            a chance to run. `CommandList` already carries `role="listbox"`
            from cmdk itself, so only the `data-state` needs adding; this
            element only renders while `isOpen` is true (`menu = isOpen ? (
            ... ) : null` above), so a static "open" is honest — there is no
            "closed" state to represent, the element is simply absent. */}
        <CommandList className="min-h-0 flex-1" style={{ maxHeight: 'none' }} data-state="open">
          <CommandEmpty>
            {isEmpty ? (
              <div className="text-left px-2 py-3 space-y-1">
                <div className="text-sm font-medium text-foreground">No slash commands yet</div>
                <div className="text-xs text-muted-foreground">
                  Add a <code className="font-mono">command:</code> field to a skill's <code className="font-mono">SKILL.md</code> frontmatter and it'll show up here.
                </div>
              </div>
            ) : (
              <span>No matching commands</span>
            )}
          </CommandEmpty>
          <CommandGroup>
            {filtered.map((c) => (
              <CommandItem
                key={c.command}
                value={c.command}
                onSelect={() => pick(c.command)}
                onMouseDown={(e) => e.preventDefault()}
                className="flex items-baseline gap-3 py-2"
              >
                <span className="font-mono text-sm text-primary shrink-0">/{c.command}</span>
                <span className="text-sm font-medium text-foreground truncate">{c.name}</span>
                <span className="ml-auto text-xs text-muted-foreground truncate max-w-[55%] text-right">
                  {c.description}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    </div>,
          // Into the surrounding dialog when there is one, not straight to the
          // body. The chat modal marks itself `aria-modal`, which tells a
          // screen reader to ignore everything OUTSIDE the dialog — a menu
          // parked on the body would be silently unreachable for that user.
          // Its own backdrop element does not clip, so this still escapes the
          // card's `overflow-hidden`, which is the whole point of the ticket.
          portalTarget,
        )}
    </>
  ) : null;

  return { isOpen, onKeyDown, menu, active, items: filtered };
}
